import React, { useEffect, useState } from "react";
import AdminLayout from "./Common/AdminLayout";
import LoadingSkeleton from "./Common/LoadingSkeleton";
import { FaEdit, FaPlus, FaTimes } from "react-icons/fa";
import { AiFillDelete } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import {
  AddQualification,
  DeleteQualification,
  EditQualification,
  FetchQualifications,
  UpdateQualification,
} from "../../Redux/Slices/Qualification";
import toast from "react-hot-toast";
import "../../asset/new/css/admin-modern.css";

const Qualification = () => {
  const [qualificationName, setQualificationName] = useState("");
  const [editData, setEditData] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { qualificationsData } = useSelector((state) => state?.Qualification);

  //Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!qualificationName.trim()) {
      toast.error("Please enter qualification name");
      return;
    }
    let inputData = { name: qualificationName };
    await AddQualification(inputData);
    dispatch(FetchQualifications());
    setQualificationName("");
    setAddModalOpen(false);
    toast.success("Qualification added successfully");
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Edit
  const handleEdit = async (id) => {
    const data = await EditQualification(id);
    setEditData(data?.data[0]);
    setEditModalOpen(true);
  };

  //Update
  const handleUpdate = async (id) => {
    if (!editData?.name?.trim()) {
      toast.error("Please enter qualification name");
      return;
    }
    let inputData = { name: editData?.name };
    await UpdateQualification(inputData, id);
    dispatch(FetchQualifications());
    setEditModalOpen(false);
    toast.success("Qualification updated successfully");
  };

  // Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this qualification?")) return;
    await DeleteQualification(id);
    dispatch(FetchQualifications());
    toast.success("Qualification deleted successfully");
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await dispatch(FetchQualifications());
      setLoading(false);
    };
    fetchData();
    document.title = "Qualifications | Admin";
  }, [dispatch]);

  // Format the date
  const formatDate = (dateString) => {
    const dateObject = new Date(dateString);
    const formattedDate = dateObject
      .toISOString()
      .replace("T", " ")
      .replace("Z", "");
    return formattedDate.slice(0, -5);
  };

  return (
    <AdminLayout
      title="Qualifications"
      subtitle="Manage qualification requirements"
      actions={
        <button
          onClick={() => setAddModalOpen(true)}
          className="admin-btn admin-btn-primary"
        >
          <FaPlus className="me-2" />
          Add Qualification
        </button>
      }
    >
      {/* Search Bar */}
      <div className="admin-card" style={{ marginBottom: 'var(--spacing-xl)' }}>
        <div className="admin-card-body">
          <input
            type="search"
            placeholder="Search qualifications..."
            className="admin-input"
            value={searchQuery}
            onChange={(e) => handleSearch(e)}
            style={{ maxWidth: "500px" }}
          />
        </div>
      </div>

      {/* Table Card */}
      <div className="admin-card">
        <div className="admin-card-body">
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Qualification Name</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <LoadingSkeleton type="table-row" count={5} columns={4} />
                ) : qualificationsData?.length > 0 ? (
                  qualificationsData
                    .filter((item) =>
                      item.name
                        .toLowerCase()
                        .includes(searchQuery.toLowerCase())
                    )
                    .map((item, index) => (
                      <tr key={item._id}>
                        <td>
                          <span className="admin-badge admin-badge-light">
                            {index + 1}
                          </span>
                        </td>
                        <td>{item?.name}</td>
                        <td>{formatDate(item?.created_at)}</td>
                        <td>
                          <div className="admin-btn-group">
                            <button
                              onClick={() => handleEdit(item?._id)}
                              className="admin-btn admin-btn-sm admin-btn-warning"
                              title="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              onClick={() => handleDelete(item?._id)}
                              className="admin-btn admin-btn-sm admin-btn-danger"
                              title="Delete"
                            >
                              <AiFillDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="4">
                      <div className="admin-empty-state">
                        <i className="fas fa-certificate fa-3x mb-3"></i>
                        <h4>No Qualifications Found</h4>
                        <p>Start by adding your first qualification.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {addModalOpen && (
        <div className="admin-modal-backdrop" onClick={() => setAddModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Add Qualification</h3>
              <button onClick={() => setAddModalOpen(false)} className="admin-modal-close">
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="admin-modal-body">
                <div className="admin-form-group">
                  <label className="admin-form-label">Qualification Name</label>
                  <input
                    type="text"
                    className="admin-input"
                    placeholder="Enter qualification name"
                    value={qualificationName}
                    onChange={(e) => setQualificationName(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button
                  type="button"
                  onClick={() => setAddModalOpen(false)}
                  className="admin-btn admin-btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="admin-btn admin-btn-primary">
                  Add Qualification
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && editData && (
        <div className="admin-modal-backdrop" onClick={() => setEditModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit Qualification</h3>
              <button onClick={() => setEditModalOpen(false)} className="admin-modal-close">
                <FaTimes />
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label className="admin-form-label">Qualification Name</label>
                <input
                  type="text"
                  className="admin-input"
                  placeholder="Enter qualification name"
                  value={editData?.name || ""}
                  onChange={(e) =>
                    setEditData({ ...editData, name: e.target.value })
                  }
                  autoFocus
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button
                onClick={() => setEditModalOpen(false)}
                className="admin-btn admin-btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdate(editData._id)}
                className="admin-btn admin-btn-primary"
              >
                Update Qualification
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Qualification;
